import { useParams, Link } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { allCategorySlugs, getArticlesByCategory } from "@/mocks/articles";
import type { ArticleData } from "@/mocks/articles";

const articleImages: Record<string, string> = {
  "article-1": "https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20community%20forum%20illustration%20with%20soft%20coral%20and%20peach%20tones%2C%20modern%20Chinese%20web%20design%20aesthetic%2C%20clean%20and%20minimal%20composition%2C%20friendly%20atmosphere%2C%20digital%20community%20concept%20art&width=800&height=400&seq=cat-art-01&orientation=landscape",
  "article-2": "https://readdy.ai/api/search-image?query=A%20group%20of%20friendly%20people%20connecting%20in%20a%20warm%20digital%20community%20space%2C%20soft%20peach%20and%20coral%20color%20palette%2C%20modern%20flat%20illustration%20style%2C%20cozy%20and%20welcoming%20atmosphere&width=800&height=400&seq=cat-art-02&orientation=landscape",
  "article-3": "https://readdy.ai/api/search-image?query=A%20fresh%20and%20modern%20digital%20platform%20update%20concept%2C%20smooth%20gradients%20in%20warm%20peach%20tones%2C%20minimalist%20UI%20elements%2C%20clean%20and%20bright%20composition&width=800&height=400&seq=cat-art-03&orientation=landscape",
  "article-4": "https://readdy.ai/api/search-image?query=A%20secure%20shield%20concept%20with%20warm%20protective%20glow%2C%20soft%20coral%20and%20amber%20accent%20colors%2C%20digital%20security%20illustration%20with%20friendly%20aesthetic&width=800&height=400&seq=cat-art-04&orientation=landscape",
  "article-5": "https://readdy.ai/api/search-image?query=A%20heartwarming%20collection%20of%20letters%20and%20messages%20floating%20in%20a%20warm%20glowing%20space%2C%20soft%20pink%20and%20coral%20tones%2C%20emotional%20connection%20concept&width=800&height=400&seq=cat-art-05&orientation=landscape",
  "article-6": "https://readdy.ai/api/search-image?query=A%20creative%20personal%20space%20customization%20concept%2C%20warm%20peach%20and%20coral%20color%20scheme%2C%20digital%20profile%20design%20illustration&width=800&height=400&seq=cat-art-06&orientation=landscape",
  "article-7": "https://readdy.ai/api/search-image?query=A%20beautiful%20travel%20destination%20illustration%20with%20warm%20peach%20and%20coral%20tones%2C%20scenic%20landscape%2C%20modern%20flat%20style%2C%20adventure%20and%20exploration%20theme&width=800&height=400&seq=cat-art-07&orientation=landscape",
  "article-8": "https://readdy.ai/api/search-image?query=A%20modern%20tech%20discussion%20illustration%20with%20warm%20coral%20accents%2C%20laptop%20and%20code%20elements%2C%20clean%20minimal%20style%2C%20friendly%20developer%20community%20vibe&width=800&height=400&seq=cat-art-08&orientation=landscape",
  "article-9": "https://readdy.ai/api/search-image?query=A%20healthy%20lifestyle%20illustration%20with%20warm%20peach%20tones%2C%20office%20stretching%20exercises%2C%20modern%20flat%20style%2C%20wellness%20and%20self%20care%20theme&width=800&height=400&seq=cat-art-09&orientation=landscape",
  "article-10": "https://readdy.ai/api/search-image?query=A%20movie%20and%20entertainment%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20film%20reel%20and%20popcorn%2C%20modern%20flat%20style%2C%20fun%20and%20vibrant&width=800&height=400&seq=cat-art-10&orientation=landscape",
  "article-11": "https://readdy.ai/api/search-image?query=A%20cozy%20solo%20living%20illustration%20with%20warm%20peach%20and%20coral%20tones%2C%20comfortable%20apartment%20scene%2C%20plants%20and%20warm%20lighting%2C%20peaceful%20and%20content%20atmosphere&width=800&height=400&seq=cat-art-11&orientation=landscape",
  "article-12": "https://readdy.ai/api/search-image?query=A%20premium%20membership%20comparison%20illustration%20with%20warm%20golden%20and%20coral%20accents%2C%20star%20ratings%20and%20features%2C%20clean%20modern%20style&width=800&height=400&seq=cat-art-12&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "qinggan-jiaoliu");
  const [sortBy, setSortBy] = useState<"latest" | "popular">("latest");

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = useMemo(
    () => allCategorySlugs.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const articles: ArticleData[] = useMemo(() => {
    const raw = getArticlesByCategory(activeSlug);
    if (sortBy === "popular") {
      return [...raw].sort((a, b) => b.readCount - a.readCount);
    }
    return [...raw].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activeSlug, sortBy]);

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16 min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "分类浏览" },
              { label: currentCategory?.name || "分类" },
            ]}
          />

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              分类浏览
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              选择你感兴趣的分类，发现更多精彩内容
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {allCategorySlugs.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-700">{articles.length}</strong> 篇文章
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => setSortBy("latest")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === "latest" ? "bg-background-50 text-foreground-900" : "text-foreground-500"
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy("popular")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === "popular" ? "bg-background-50 text-foreground-900" : "text-foreground-500"
                }`}
              >
                最热
              </button>
            </div>
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={articleImages[article.id]}
                      alt={article.title}
                      title={`${article.title} - 爱情岛论坛`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="text-xs font-medium bg-primary-50 text-primary-600 px-2.5 py-1 rounded-full">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 leading-snug mb-3 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i> {article.readCount.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line"></i> {article.commentCount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}